import axios from "axios";
import { clearRefreshToken, loadRefreshToken, saveRefreshToken } from "@/lib/refreshStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Access token lives in memory only (Story 2.3 / FR18). The refresh token is an
// httpOnly cookie plus, on iOS/PWA, a copy in localStorage — WebKit drops
// cross-origin Set-Cookie from fetch when the app is killed.
let accessToken = null;
let authGeneration = 0;
// Impersonation is deliberately memory-only.  The admin token is never put in
// storage and the target account never receives a refresh token or cookie.
let impersonation = null;
export function setAccessToken(t) { accessToken = t || null; authGeneration += 1; }
export function getAccessToken() { return accessToken; }
export function isImpersonating() { return Boolean(impersonation); }
export function getImpersonation() { return impersonation; }
export function startImpersonation({ token, user, expiresAt }) {
  if (!token) throw new Error("Token de impersonação ausente");
  impersonation = { adminToken: accessToken, user, expiresAt, token };
  accessToken = token;
  authGeneration += 1;
  return impersonation;
}
export function stopImpersonation() {
  if (!impersonation) return false;
  accessToken = impersonation.adminToken || null;
  impersonation = null;
  authGeneration += 1;
  return true;
}

export function captureRefreshToken(data) {
  const raw = data?.refresh_token;
  if (raw) saveRefreshToken(raw);
}

function refreshHeaders() {
  const headers = { "Content-Type": "application/json" };
  // Never send the admin's iOS fallback refresh token while acting as a target.
  const stored = !isImpersonating() && loadRefreshToken();
  if (stored) headers["X-Refresh-Token"] = stored;
  return headers;
}

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  cfg.headers = cfg.headers || {};
  cfg._requestAccessToken = accessToken;
  if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
  const stored = !isImpersonating() && loadRefreshToken();
  if (stored && !cfg.headers["X-Refresh-Token"]) cfg.headers["X-Refresh-Token"] = stored;
  return cfg;
});

// Single in-flight refresh shared by all callers (avoids a refresh stampede when
// several requests 401 at once). POST /api/auth/refresh mints a new access token
// from the cookie, or X-Refresh-Token on iOS standalone.
let refreshPromise = null;
export function refreshAccessToken() {
  // A request started as an impersonated user must never mint or adopt an
  // admin token. Returning null makes callers surface their original error.
  if (isImpersonating()) return Promise.resolve(null);
  if (!refreshPromise) {
    const generationAtStart = authGeneration;
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true, headers: refreshHeaders() })
      .then((r) => {
        // A refresh begun as admin may settle after an impersonation swap. It
        // must never overwrite the target bearer or update refresh storage.
        if (isImpersonating() || generationAtStart !== authGeneration) return null;
        const t = r.data?.access_token || r.data?.token || null;
        setAccessToken(t);
        captureRefreshToken(r.data);
        return t;
      })
      .catch((err) => {
        if (err?.response?.status === 401) clearRefreshToken();
        throw err;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

function expireSession() {
  if (isImpersonating()) {
    stopImpersonation();
    if (typeof window !== "undefined") window.dispatchEvent(new Event("valiosia:impersonation-expired"));
    return;
  }
  const had = Boolean(accessToken);
  setAccessToken(null);
  clearRefreshToken();
  if (had && typeof window !== "undefined") {
    window.dispatchEvent(new Event("valiosia:session-expired"));
  }
}

function accessTokenTtlMs() {
  if (!accessToken) return 0;
  try {
    const part = accessToken.split(".")[1];
    if (!part) return 0;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (part.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return (Number(payload.exp) || 0) * 1000 - Date.now();
  } catch {
    return 0;
  }
}

// Chat streams last minutes; the 1h JWT dies mid-turn if we wait for a 401.
const REFRESH_SKEW_MS = 60 * 1000;
export async function ensureFreshAccessToken() {
  if (isImpersonating()) return accessToken;
  if (accessTokenTtlMs() > REFRESH_SKEW_MS) return accessToken;
  try {
    return await refreshAccessToken();
  } catch {
    return accessToken;
  }
}

// Public auth endpoints where a 401/4xx is the REAL answer (wrong password,
// expired link) — refreshing the session would be pointless and only delays
// the caller's error handling.
const PUBLIC_AUTH_PATHS = [
  "/auth/login", "/auth/signup", "/auth/refresh", "/auth/magic-link",
  "/auth/magic-login", "/auth/forgot-password", "/auth/reset-password",
];

// One transparent retry on 401: refresh then replay the original request.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config;
    const status = error.response?.status;
    const isRefreshCall = PUBLIC_AUTH_PATHS.some((p) => cfg?.url?.includes(p));
    // An impersonated bearer expires independently of the admin session. End
    // it only when this response belongs to the current target identity; a
    // late response from before a token swap must have no side effect.
    if (status === 401 && cfg && isImpersonating() && cfg._requestAccessToken === accessToken) {
      expireSession();
      return Promise.reject(error);
    }
    // Do not replay an action after identity changed, including an in-flight
    // admin request which completes once impersonation begins.
    const sameIdentity = cfg?._requestAccessToken === accessToken;
    if (status === 401 && cfg && !cfg._retry && !isRefreshCall && !isImpersonating() && sameIdentity) {
      cfg._retry = true;
      try {
        const t = await refreshAccessToken();
        if (t) {
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${t}`;
          return api(cfg);
        }
      } catch {
        // Mid-session expiry: the refresh cookie is dead. Tell the shell once
        // (AuthContext clears the user -> ProtectedRoute sends her to /login)
        // instead of leaving a broken page behind the caller's error toast.
        expireSession();
      }
    }
    return Promise.reject(error);
  }
);

// Rebuild an axios-shaped error from a fetch Response so friendlyError can
// resolve the backend's structured {code}/detail instead of a generic string.
async function fetchError(res, fallbackMessage) {
  const data = await res.json().catch(() => null);
  const err = new Error(fallbackMessage);
  err.response = { status: res.status, data };
  return err;
}

// Authenticated binary download of an artifact PDF (§9.11). Uses fetch (not axios)
// so we get a real Blob, then triggers a browser download. Retries once on 401
// by minting a fresh access token, mirroring the axios interceptor behaviour.
// `working` baixa a versão em andamento (revisão) que o painel da conversa mostra.
export async function downloadArtifactPdf(artifactId, filename = "roteiro.pdf", { working = false } = {}, _retry = true) {
  const qs = working ? "?working=true" : "";
  const res = await fetch(`${API_BASE}/artifacts/${artifactId}/pdf${qs}`, {
    method: "GET",
    credentials: "include",
    headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
  });
  if (res.status === 401 && _retry && !isImpersonating()) {
    const t = await refreshAccessToken().catch(() => null);
    if (t) return downloadArtifactPdf(artifactId, filename, { working }, false);
  }
  if (res.status === 401 && isImpersonating()) expireSession();
  if (!res.ok) throw await fetchError(res, "Falha ao gerar o PDF");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filenameFromDisposition(res.headers.get("content-disposition"), filename);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const star = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1]); } catch { /* ignore */ }
  }
  const plain = header.match(/filename="([^"]+)"/i);
  return plain ? plain[1] : fallback;
}

export async function downloadArtifactTables(artifactId, { kind = "xlsx", mode = "tables", working = false, filename } = {}, _retry = true) {
  const qs = new URLSearchParams();
  if (kind === "xlsx") qs.set("mode", mode);
  if (working) qs.set("working", "true");
  const path = kind === "csv" ? "csv" : "xlsx";
  const res = await fetch(`${API_BASE}/artifacts/${artifactId}/${path}?${qs.toString()}`, {
    method: "GET",
    credentials: "include",
    headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
  });
  if (res.status === 401 && _retry && !isImpersonating()) {
    const t = await refreshAccessToken().catch(() => null);
    if (t) return downloadArtifactTables(artifactId, { kind, mode, working, filename }, false);
  }
  if (res.status === 401 && isImpersonating()) expireSession();
  if (!res.ok) throw await fetchError(res, "Falha ao gerar as tabelas");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filenameFromDisposition(res.headers.get("content-disposition"), filename || (kind === "csv" ? "tabelas.csv" : "tabelas.xlsx"));
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// `signal` permite interromper a resposta em curso (botão Parar). Abortar fecha o
// stream no cliente; o backend já persistiu o que produziu até ali.
export async function streamChat(threadId, content, onEvent, signal, _retry = true) {
  await ensureFreshAccessToken();
  const res = await fetch(`${API_BASE}/threads/${threadId}/send`, {
    method: "POST",
    credentials: "include",
    signal,
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ content }),
  });
  if (res.status === 401 && _retry && !isImpersonating()) {
    const t = await refreshAccessToken().catch(() => null);
    if (t) return streamChat(threadId, content, onEvent, signal, false);
    expireSession();
  }
  if (res.status === 401 && isImpersonating()) expireSession();
  if (!res.ok) throw await fetchError(res, "Falha ao enviar mensagem");
  if (!res.body) throw new Error("Falha ao enviar mensagem");
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const raw = line.slice(6).trim();
      if (raw === "[DONE]") return;
      try {
        const evt = JSON.parse(raw);
        onEvent(evt);
      } catch {
        // ignore malformed
      }
    }
  }
}
